import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useMutation, gql } from '@apollo/client';
import classNames from 'classnames';
import PulseLoader from 'react-spinners/PulseLoader';
import Collapse from '@material-ui/core/Collapse';
import { stringify } from 'qs';
import { sendIntercomEvent } from 'services/analytics';

// app: actions
import { pollUser } from 'actions/user/refreshUser';
import { loadSearchView } from 'actions/search';

// app: components
import AlacarteOffer from 'components/AlacarteOffer';
import Link from 'components/Link';
import PaddleCheckout from 'components/PaddleCheckout';
import TopRecipes from 'components/TopRecipes';

// app: services
import { isApp } from 'services/environment';
import history from 'services/history';
import route from 'services/route';

// app: constants
import { COLOURS } from 'constants/index';

// app: queries
import GetAlacarteCheckout from './GetAlacarteCheckout.gql';

// assets
import CssAlacarteCheckout from './AlacarteCheckout.scss';

const REFRESH_TOP_BOOKS_MUTATION = gql`
  mutation RefreshTopBooks {
    RefreshTopBooks
  }
`;

function AlacarteCheckout(props) {
  const { className, isbn } = props;
  useStyles(CssAlacarteCheckout);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);

  const dispatch = useDispatch();
  const userLoaded = useSelector(state => state.user.logged_in && state.user.id > 0);
  const purchaseAvailable = useSelector(state => state.user.owned_books.includes(isbn));
  const country = useSelector(state => state.paywall.country || state.user.geolocation.countryCode);

  const [refreshTopBooks] = useMutation(REFRESH_TOP_BOOKS_MUTATION);

  const { data, previousData } = useQuery(GetAlacarteCheckout, {
    variables: {
      isbn,
      country,
      _authenticated: true,
    },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    ssr: false,
    skip: !userLoaded || isApp(),
  });

  const book = data?.Book || previousData?.Book || {};

  // The value of the checkout link can change if the user changes their country from within the checkout
  // because the state.paywall.country value will change. We will memoise the initial checkout link to avoid
  // reloading the checkout.
  const checkoutLinkWasLoaded = !!book.checkout_link;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkoutLink = useMemo(() => book.checkout_link, [checkoutLinkWasLoaded]);
  const failed = book.isbn && book.alacarte_available && !book.checkout_link;

  // When we see the book is available refresh the user's top book list.
  useEffect(
    () => {
      if (purchaseAvailable) {
        refreshTopBooks({
          variables: {
            _authenticated: true,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [purchaseAvailable],
  );

  const handleCheckoutSuccess = event => {
    dispatch(pollUser());
    setCheckoutComplete(true);
    const intercomEvent = {
      title: book.title,
    };
    const price = event?.checkout?.prices?.customer;
    if (price) {
      intercomEvent.currency = price.currency;
      intercomEvent.total = price.total;
      intercomEvent.total_tax = price.total_tax;
    }
    if (event?.user) {
      intercomEvent.country = event.user.country;
    }
    sendIntercomEvent('alacarte-purchase', intercomEvent);
  };

  const handleCheckoutLoadComplete = () => {
    setCheckoutLoaded(true);
  };

  const handleViewMore = () => {
    dispatch(loadSearchView());
    const { title } = book;
    const queryParams = stringify(
      { q: 'recipes', 'book_full_title[0]': title },
      { encodeValuesOnly: true },
    );
    setTimeout(() => history.push(`/search?${queryParams}`), 0);
  };

  return (
    <div className={classNames('alc-checkout', className)}>
      <div className="alc-checkout__col">
        <AlacarteOffer
          purchasing
          purchaseComplete={purchaseAvailable}
          hideButton
          isbn={isbn}
          withAlacarteIndicator={purchaseAvailable}
        />
      </div>
      {checkoutComplete || purchaseAvailable ? (
        <div
          className={classNames('alc-checkout__complete', {
            'alc-checkout__complete--centered': !purchaseAvailable,
          })}
        >
          <p>
            {purchaseAvailable ? (
              <React.Fragment>
                <p className="margin-bottom-5">
                  <strong>Thank you for your purchase</strong>
                </p>
                <p className="margin-bottom-5">
                  We will email you a receipt confirming your order.
                </p>
                <p className="margin-bottom-5">
                  You now have full access to{' '}
                  <Link
                    className="link link--energized"
                    to={route.book(book.isbn, book.signature_)}
                  >
                    {book.title}
                  </Link>. Here are the most popular recipes:
                </p>
              </React.Fragment>
            ) : (
              <p>Waiting for confirmation</p>
            )}
          </p>
          {purchaseAvailable ? (
            <div>
              <TopRecipes hideHeading isbn={isbn} onViewMore={handleViewMore} />
            </div>
          ) : (
            <PulseLoader loading color={COLOURS.ENERGIZED} sizeUnit="px" size={8} />
          )}
        </div>
      ) : (
        <div className="margin-top-10 alc_checkout__checkout">
          {!isApp() ? (
            <Collapse in={checkoutLoaded}>
              {book.checkout_link ? (
                <PaddleCheckout
                  override={checkoutLink}
                  onCheckoutSuccess={handleCheckoutSuccess}
                  loadCallback={handleCheckoutLoadComplete}
                />
              ) : null}
            </Collapse>
          ) : null}
          {isApp() || checkoutLoaded || failed || book?.alacarte_available === false ? null : (
            <PulseLoader loading color={COLOURS.ENERGIZED} sizeUnit="px" size={8} />
          )}
          {failed ? (
            <p>
              This book isn&apos;t available for to buy right now due to technical issues. Check
              back soon.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

AlacarteCheckout.propTypes = {
  className: PropTypes.string,
  isbn: PropTypes.string.isRequired,
};

AlacarteCheckout.defaultProps = {
  className: null,
};

export default AlacarteCheckout;
